import { cloneDeep } from 'lodash';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { CurrentTransactionInfo, Transaction, TransactionsByAccount } from './types';
// utils
import {
  isTransactionCompletedSuccessfully,
  isTransactionCompletedWithFailure,
  isTransactionPending,
} from './utils';

const DEFAULT_TRANSACTIONS: Transaction[] = [];

interface TransactionStore {
  currentTransaction: CurrentTransactionInfo | null;
  resetCurrentTransaction: () => void;
  transactions: TransactionsByAccount;
  addTransaction: (tx: Transaction, address: string, chainId: number) => void;
  completeTransactions: (receipt: Transaction[], address: string, chainId: number) => void;
  getPendingTransactions: (address: string, chainId: number) => Transaction[];
}

export function pruneTransactions(transactions: TransactionsByAccount) {
  const now = Date.now();
  const cutoff = now - 30 * 60 * 1000; // 30 minutes

  const cloned = cloneDeep(transactions) || {};

  for (const account in cloned) {
    for (const chainId in cloned[account]) {
      const nChainId = Number(chainId);

      if (Number.isNaN(nChainId)) {
        // this just assists to help typescript out
        continue;
      }
      //  I hate all this yelling at typescript but it's a necessary evil, it is unable to infer the type correctly
      cloned![account]![nChainId] = cloned![account]![nChainId]!.filter(
        (tx) => new Date(tx.timestamp).getTime() > cutoff,
      );
      if (cloned![account]![nChainId]!.length === 0) {
        delete cloned![account]![nChainId];
      }
    }
    if (Object.keys(cloned![account] || {}).length === 0) {
      delete cloned![account];
    }
  }
  return cloned || {};
}

export const useTransactionStore = createWithEqualityFn(
  persist<TransactionStore>(
    (set, get) => ({
      currentTransaction: null,
      resetCurrentTransaction() {
        set({ currentTransaction: null });
      },
      transactions: {},
      addTransaction(tx: Transaction, address: string, chainId: number) {
        const transactions = get().transactions;
        const newCurrentTransaction = {
          tx,
          address,
          chainId,
          isSuccessful: false,
          isFailed: false,
        };

        const newTransactions = {
          ...(transactions || {}),
          [address]: {
            ...(transactions?.[address] || {}),
            [chainId]: [...(transactions?.[address]?.[chainId] || []), { ...tx }],
          },
        };

        set({
          currentTransaction: newCurrentTransaction,
          transactions: pruneTransactions(newTransactions),
        });
      },
      completeTransactions(receipts: Transaction[], address: string, chainId: number) {
        const transactions = get().transactions;
        const newTransactions = {
          ...(transactions || {}),
          [address]: {
            ...(transactions?.[address] || {}),
            [chainId]: (transactions?.[address]?.[chainId] || []).map(
              (transaction: Transaction): Transaction => {
                // Find the receipt that matches the current transaction
                const receipt = receipts.find(
                  (receipt) =>
                    isTransactionCompletedSuccessfully(transaction, receipt) ||
                    isTransactionCompletedWithFailure(transaction, receipt),
                );
                // If no receipt was found, return the transaction as is
                if (!receipt) return transaction;
                // Update the transaction with the receipt
                return {
                  ...transaction,
                  ...receipt,
                  transactionType: transaction.transactionType,
                } as Transaction;
              },
            ),
          },
        };

        // Update the current transaction if it matches one of the receipts
        const currentTransaction = get().currentTransaction;
        let newCurrentTransaction = currentTransaction ? { ...currentTransaction } : null;
        if (newCurrentTransaction) {
          const successfulReceipt = receipts.find((receipt) =>
            isTransactionCompletedSuccessfully(newCurrentTransaction!.tx, receipt),
          );
          newCurrentTransaction.isSuccessful = !!successfulReceipt;
          const failedReceipt = receipts.find((receipt) =>
            isTransactionCompletedWithFailure(newCurrentTransaction!.tx, receipt),
          );
          newCurrentTransaction.isFailed = !!failedReceipt;
        }

        set({
          currentTransaction: newCurrentTransaction,
          transactions: pruneTransactions(newTransactions),
        });
      },
      getPendingTransactions(address: string, chainId: number): Transaction[] {
        if (!address || !chainId) return DEFAULT_TRANSACTIONS;

        const transactions = get().transactions[address]?.[chainId];

        if (!transactions) return DEFAULT_TRANSACTIONS;

        return transactions.filter((transaction: Transaction) =>
          transaction.hash ? isTransactionPending(transaction) : false,
        );
      },
    }),
    {
      name: 'matcha-transactions',
      onRehydrateStorage: (state) => {
        // keep transactions from the last 30 minutes
        const transactions = state.transactions;

        state.transactions = pruneTransactions(transactions);
      },
    },
  ),
  shallow,
);

// selectors
export const resetCurrentTransactionSelector = (state: TransactionStore) =>
  state.resetCurrentTransaction;
export const addTransactionSelector = (state: TransactionStore) => state.addTransaction;
export const pendingTransactionSelectors = (state: TransactionStore) => ({
  completeTransactions: state.completeTransactions,
  getPendingTransactions: state.getPendingTransactions,
});
export const transactionsSelectors = ({
  currentTransaction,
  transactions,
  getPendingTransactions,
}: TransactionStore) => ({
  currentTransaction,
  transactions,
  getPendingTransactions,
});
export const transactionsSelector = (state: TransactionStore) => state.transactions;
export const completeTransactionsSelector = (state: TransactionStore) => state.completeTransactions;
